<template>
  <div class="c-root" :class="{ 'is-embed': embed }">
    <div class="c-main">
      <!-- 左：聊天区 -->
      <div class="c-chat">
        <div class="c-chat-head">
          <div class="c-chat-title">专业咨询对话</div>
          <span class="c-restart" @click="restart">重新开始</span>
        </div>

        <div class="c-msgs" ref="scroll">
          <div
            v-for="m in messages"
            :key="m.id"
            class="c-msg"
            :class="'is-' + m.role"
          >
            <div class="c-avatar" v-if="m.role !== 'user'">{{ avatarOf(m.role) }}</div>
            <div class="c-bubble">
              <div class="c-bubble-tag" v-if="m.role === 'guide'">补充信息</div>
              <div class="c-bubble-tag warn" v-if="m.role === 'reject'">超出受理范围</div>
              <div class="c-bubble-tag warn" v-if="m.role === 'error'">服务提示</div>
              <div class="c-bubble-tag" v-if="m.role === 'notice'">系统提示</div>
              <div class="c-md" v-html="m.html"></div>
              <span class="c-caret" v-if="m.streaming"></span>
              <div class="c-bubble-foot" v-if="m.role === 'ai' && !m.streaming && m.text">
                <span class="c-foot-warn">以上内容仅为建议，落地请咨询专业机构</span>
              </div>
            </div>
            <div class="c-avatar user" v-if="m.role === 'user'">我</div>
          </div>

          <!-- 思考中提醒 -->
          <div class="c-msg is-ai" v-if="phase === 'analyzing' || phase === 'thinking'">
            <div class="c-avatar">答</div>
            <div class="c-bubble thinking">
              <span class="dots"><i></i><i></i><i></i></span>
              <span class="thinking-text">{{ thinkingText }}</span>
            </div>
          </div>
        </div>

        <!-- 快捷选项 -->
        <div class="c-picker" v-if="pendingOptions">
          <div class="c-picker-title">{{ pendingOptions.title }}</div>
          <div class="c-chips">
            <span
              v-for="opt in pendingOptions.items"
              :key="opt.value"
              class="c-chip"
              @click="pickOption(pendingOptions.key, opt)"
            >{{ opt.label }}</span>
          </div>
        </div>

        <!-- 输入区 -->
        <div class="c-input">
          <textarea
            v-model="input"
            :placeholder="placeholder"
            rows="3"
            @keydown.enter.exact.prevent="send"
          ></textarea>
          <div class="c-input-foot">
            <span class="c-input-tip">回车发送 / Shift+回车换行 · 输出仅为建议，不构成落地执行方案</span>
            <div class="c-btns">
              <button class="btn-ghost" v-if="busy" @click="stop">停止生成</button>
              <button class="btn-primary" :disabled="busy" @click="send">
                {{ busy ? '生成中…' : '发送' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 右：要素面板（内嵌模式不展示，要素选择由对话内快捷选项驱动，与小程序一致） -->
      <aside class="c-side" v-if="!embed">
        <div class="c-side-card">
          <div class="c-side-title">本次咨询要素</div>
          <p class="c-side-desc">五项信息齐全后才会输出分析结果，缺失项会在对话中引导补充。</p>

          <div class="c-slot" v-for="s in slotView" :key="s.key">
            <div class="c-slot-head">
              <span class="c-slot-name">{{ s.name }}</span>
              <span class="c-slot-state" :class="s.value ? 'done' : 'todo'">{{ s.value ? '已填写' : '待补充' }}</span>
            </div>
            <div class="c-slot-value" v-if="s.value">{{ s.value }}</div>
            <div class="c-slot-empty" v-else>{{ s.hint }}</div>
            <div class="c-slot-ops" v-if="s.value">
              <span @click="resetSlot(s.key)">更换</span>
            </div>
          </div>

          <div class="c-side-block">
            <div class="c-side-sub">公司规模（点击选择）</div>
            <div class="c-chips small">
              <span
                v-for="opt in scaleOptions"
                :key="opt.value"
                class="c-chip"
                :class="{ active: slots.scale === opt.value }"
                @click="pickOption('scale', opt)"
              >{{ opt.label }}</span>
            </div>
          </div>

          <div class="c-side-block">
            <div class="c-side-sub">专业服务领域（点击选择）</div>
            <div class="c-chips small">
              <span
                v-for="opt in domainOptions"
                :key="opt.value"
                class="c-chip"
                :class="{ active: slots.domain === opt.value }"
                @click="pickOption('domain', opt)"
              >{{ opt.label }}</span>
            </div>
          </div>

          <div class="c-side-block" v-if="samples.length">
            <div class="c-side-sub">试试这样问</div>
            <div class="c-sample" v-for="s in samples" :key="s" @click="useSample(s)">{{ s }}</div>
          </div>
        </div>

        <div class="c-side-card note">
          <div class="c-side-title">落地务必找专业机构</div>
          <ul class="c-note-list">
            <li>目标国投资促进机构 / 公司注册局</li>
            <li>中国驻当地使领馆经济商务处</li>
            <li>中国贸促会及地方分会</li>
            <li>目标市场执业律师事务所</li>
            <li>具备当地资质的会计师事务所</li>
          </ul>
          <div class="c-note-tip">{{ disclaimer }}</div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { fetchConsultMeta, analyzeConsultInput, streamConsultAnswer } from '../api/consultAi'

// ---------- 轻量 Markdown 渲染（先转义，再做有限的标记替换，避免 XSS） ----------
function escapeHtml (str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function inlineMd (str) {
  return str
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/(https?:\/\/[^\s，。、）)"']+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
}

function renderMd (text) {
  if (!text) return ''
  const lines = escapeHtml(text).split('\n')
  let html = ''
  let inUl = false
  const closeUl = () => {
    if (inUl) {
      html += '</ul>'
      inUl = false
    }
  }
  lines.forEach(raw => {
    const line = raw.trim()
    if (!line) {
      closeUl()
      return
    }
    if (/^####\s+/.test(line)) {
      closeUl()
      html += '<h5>' + inlineMd(line.replace(/^####\s+/, '')) + '</h5>'
    } else if (/^###\s+/.test(line)) {
      closeUl()
      html += '<h4>' + inlineMd(line.replace(/^###\s+/, '')) + '</h4>'
    } else if (/^#{1,2}\s+/.test(line)) {
      closeUl()
      html += '<h3>' + inlineMd(line.replace(/^#{1,2}\s+/, '')) + '</h3>'
    } else if (/^[-*•]\s+/.test(line)) {
      if (!inUl) {
        html += '<ul>'
        inUl = true
      }
      html += '<li>' + inlineMd(line.replace(/^[-*•]\s+/, '')) + '</li>'
    } else if (/^\d+[.、)）]\s*/.test(line)) {
      // 保留模型自带编号，避免 <ol> 二次编号
      closeUl()
      html += '<p class="md-num">' + inlineMd(line) + '</p>'
    } else {
      closeUl()
      html += '<p>' + inlineMd(line) + '</p>'
    }
  })
  closeUl()
  return html
}

const DEFAULT_SCALES = [
  { value: 'startup', label: '初创/小微企业（100人以下）' },
  { value: 'growth', label: '成长型企业（100-1000人）' },
  { value: 'large', label: '大型企业（1000人以上）' }
]

const DEFAULT_DOMAINS = [
  { value: '法律', label: '法律' },
  { value: '财务', label: '财务' },
  { value: '税务', label: '税务' },
  { value: '合规', label: '合规' },
  { value: '知识产权', label: '知识产权' },
  { value: 'ODI', label: 'ODI' },
  { value: '结汇', label: '结汇' }
]

const THINKING_STAGES = [
  '正在解析您的咨询要素…',
  '正在梳理问题内容…',
  '正在生成答案中…'
]

// 命中这些关键词的提示属内部过程（要素变更、模型切换、重试等），一律不展示给用户
const INTERNAL_NOTICE_WORDS = [
  '已更新', '已自动切换', '切换至', '重新生成', '重试', '模型', '槽位', '已清空'
]

const SLOT_META = [
  { key: 'question', name: '主要问题', hint: '例如：设立子公司需要办理哪些许可与手续' },
  { key: 'country', name: '目标国家 / 区域', hint: '例如：越南、阿联酋、欧盟' },
  { key: 'industry', name: '所属行业', hint: '例如：跨境电商、汽车及零部件' },
  { key: 'scale', name: '公司规模', hint: '初创/小微、成长型、大型' },
  { key: 'domain', name: '专业服务领域', hint: '法律 / 财务 / 税务 / 合规 / 知识产权 / ODI / 结汇' }
]

export default {
  name: 'AiConsultChat',
  props: {
    // 内嵌模式：隐藏侧边要素面板、充满父容器高度，用于小程序 web-view 嵌入
    embed: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      sessionId: 'ai_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
      samples: [
        '我们想在越南设厂，财务上要做哪些备案和资金安排？',
        '跨境电商出海欧盟，税务方面要注意什么？',
        '在阿联酋设立子公司，外资持股和合规有哪些要求？'
      ],
      scaleOptions: DEFAULT_SCALES.slice(),
      domainOptions: DEFAULT_DOMAINS.slice(),
      countryOptions: [],
      industryOptions: [],
      thinkingDelayMs: 5000,
      disclaimer: '本页面所有输出均为参考建议，不构成落地执行方案，正式落地前请由目标市场执业机构与持牌专业顾问出具正式意见。',
      slots: { question: '', country: '', industry: '', scale: '', scaleLabel: '', domain: '' },
      messages: [],
      pendingOptions: null,
      input: '',
      phase: 'idle', // idle | analyzing | thinking | streaming
      thinkingText: THINKING_STAGES[0],
      thinkingIdx: 0,
      thinkingTimer: null,
      typeStartTimer: null,
      typeTimer: null,
      buffer: '',
      streamEnded: false,
      streamCtl: null,
      askStart: 0,
      currentId: '',
      pendingError: '',
      seq: 0,
      // 多轮上下文：已完成轮次的问答（{ role: 'user' | 'assistant', content }），提问时回传给后端，
      // 使「那如果是德国呢」「规模改成大型」这类追问能被正确理解。
      history: [],
      // 本轮用户输入的原始文本，答案生成完成后连同回答一起写入 history
      pendingUserText: '',
      // 用户主动「停止生成」时跳过写入上下文，避免残缺答案污染后续追问
      skipHistory: false,
      // 点「更换」清空要素时暂存的旧值 { key, old }，待选新值后同步替换问题描述里的旧要素
      pendingReplace: null
    }
  },
  computed: {
    busy () {
      return this.phase !== 'idle'
    },
    placeholder () {
      return '请描述你的出海问题，并尽量包含：目标国家/区域、所属行业、公司规模、专业服务领域。\n例如：我们是 300 人的汽车零部件公司，想在墨西哥设厂，财务上要做哪些备案？'
    },
    slotView () {
      return SLOT_META.map(m => ({
        key: m.key,
        name: m.name,
        hint: m.hint,
        value: m.key === 'scale' ? this.slots.scaleLabel : (m.key === 'domain' ? this.slots.domain : this.slots[m.key])
      }))
    }
  },
  mounted () {
    this.greet()
    this.loadMeta()
  },
  beforeDestroy () {
    this.clearTimers()
    if (this.streamCtl) this.streamCtl.abort()
  },
  methods: {
    async loadMeta () {
      try {
        const meta = await fetchConsultMeta()
        if (meta.scaleOptions && meta.scaleOptions.length) this.scaleOptions = meta.scaleOptions
        if (meta.domainOptions && meta.domainOptions.length) this.domainOptions = meta.domainOptions
        if (meta.countryOptions) this.countryOptions = meta.countryOptions
        if (meta.industryOptions) this.industryOptions = meta.industryOptions
        if (meta.thinkingDelayMs) this.thinkingDelayMs = meta.thinkingDelayMs
        if (meta.disclaimer) this.disclaimer = meta.disclaimer
      } catch (e) {
        // 元数据失败不阻塞使用，沿用本地默认选项
      }
    },
    // 开场白：能力说明 + 所需信息 + 免责声明，全部通过对话引导
    greet () {
      this.pushMsg('ai', [
        '我是「企业出海专业服务咨询助手」，可为您出海提供参考建议。',
        '为给您更精准的答复，请尽量在提问时包含以下信息；若未提及，我会在对话中逐一向您确认：',
        '1. 主要问题内容',
        '2. 目标国家 / 区域',
        '3. 公司所属行业',
        '4. 公司规模（初创/小微、成长型、大型 等）',
        '5. 专业服务领域（法律 / 财务 / 税务 / 合规 / 知识产权 / ODI / 结汇 等）',
        '',
        '⚠️ 所有回答仅为参考建议，来源均来自政府及权威部门公开规则；落地执行请咨询专业机构，或注册账号后联系客服。'
      ].join('\n'))
    },
    avatarOf (role) {
      if (role === 'guide') return '?'
      if (role === 'reject') return '!'
      if (role === 'error' || role === 'notice') return 'i'
      return '答'
    },
    pushMsg (role, text) {
      this.seq += 1
      const msg = {
        id: role + '_' + this.seq,
        role,
        text: text || '',
        html: renderMd(text || ''),
        streaming: false,
        model: ''
      }
      this.messages.push(msg)
      this.scrollBottom()
      return msg
    },
    findMsg (id) {
      for (let i = 0; i < this.messages.length; i++) {
        if (this.messages[i].id === id) return this.messages[i]
      }
      return null
    },
    scrollBottom () {
      this.$nextTick(() => {
        const el = this.$refs.scroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    useSample (text) {
      if (this.busy) return
      this.input = text
    },
    resetSlot (key) {
      if (this.busy) return
      // 暂存被清空的旧值：用户选定新值后同步替换问题描述里的旧要素，
      // 避免「目标国家已改成泰国、问题里仍写越南」这类自相矛盾的提问
      const old = key === 'scale' ? this.slots.scaleLabel : this.slots[key]
      this.pendingReplace = { key: key, old: old || '' }
      this.slots[key] = ''
      if (key === 'scale') this.slots.scaleLabel = ''
      if (key === 'domain') this.slots.domain = ''
      // 清空后直接弹出选项供重新选择，不再输出「已清空…」类系统提示
      this.showPicker([key])
    },
    restart () {
      if (this.streamCtl) this.streamCtl.abort()
      this.clearTimers()
      this.slots = { question: '', country: '', industry: '', scale: '', scaleLabel: '', domain: '' }
      this.messages = []
      this.history = []
      this.pendingUserText = ''
      this.skipHistory = false
      this.pendingReplace = null
      this.pendingOptions = null
      this.input = ''
      this.buffer = ''
      this.phase = 'idle'
      this.greet()
    },
    // ---------- 选项（后端引导补充要素） ----------
    showPicker (missing, options) {
      const key = (missing || []).filter(k => k !== 'question')[0]
      if (!key) {
        this.pendingOptions = null
        return
      }
      let items = options && options[key] ? options[key] : null
      if (!items) {
        if (key === 'scale') items = this.scaleOptions
        else if (key === 'country') items = this.countryOptions
        else if (key === 'industry') items = this.industryOptions
        else if (key === 'domain') items = this.domainOptions
      }
      if (!items || !items.length) {
        this.pendingOptions = null
        return
      }
      const titleMap = {
        country: '请选择目标国家 / 区域（也可直接在下方输入框输入后发送）',
        industry: '请选择所属行业（也可直接在下方输入框输入后发送）',
        scale: '请选择公司规模（也可直接在下方输入框输入后发送）',
        domain: '请选择专业服务领域（也可直接在下方输入框输入后发送）'
      }
      this.pendingOptions = { key, title: titleMap[key] || '请选择', items }
      this.scrollBottom()
    },
    pickOption (key, opt) {
      if (this.busy) return
      this.slots[key] = opt.value
      if (key === 'scale') this.slots.scaleLabel = opt.label
      if (key === 'domain') this.slots.domain = opt.value
      // 若此前点过「更换」，把问题描述里残留的旧要素替换成新值，保持要素与描述一致
      const rep = this.pendingReplace
      if (rep && rep.key === key && rep.old && this.slots.question) {
        const nv = key === 'scale' ? opt.label : opt.value
        this.slots.question = this.slots.question.split(rep.old).join(nv)
      }
      this.pendingReplace = null
      this.pushMsg('user', key === 'domain' ? opt.value : opt.label)
      this.pendingOptions = null
      this.pendingUserText = key === 'domain' ? opt.value : opt.label
      this.flow('')
    },
    // ---------- 主流程 ----------
    // 判断输入是否为「短回答」（用于 pending 要素确定性直填）：
    // 含疑问/诉求词或较长视为新问题，仍交由后端抽取；否则视为对当前追问要素的直接回答。
    isShortAnswer (t) {
      const hints = ['怎么', '怎样', '如何', '哪些', '什么', '多久', '多少', '是否', '能不能', '可以吗',
        '需要', '要求', '流程', '手续', '条件', '注意事项', '风险', '规定', '政策', '限制', '？', '?', '。', '，']
      const effLen = t.replace(/[\s，。,.、；;：:？?！!~…（）()-]/g, '').length
      if (effLen > 15) return false
      if (hints.some(h => t.indexOf(h) !== -1)) return false
      return true
    },
    send () {
      if (this.busy) return
      const text = this.input.trim()
      if (!text) {
        this.$toast.show({ message: '请先输入您的问题', type: 'error' })
        return
      }
      this.pushMsg('user', text)
      const pending = this.pendingOptions
      // 后端正在追问某个要素（如专业服务领域）时，主输入框内容直接作为该要素的确定性回答，
      // 不再依赖后端 AI 抽取（避免「安全」这类短词抽不到而反复提示「请补充专业服务领域」）。
      // 仅当输入像「短回答」时才直填；像新问题 / 长句则仍走后端全量抽取，避免误判。
      if (pending && pending.key && pending.key !== 'question' && this.isShortAnswer(text)) {
        const newSlots = Object.assign({}, this.slots, { [pending.key]: text })
        if (pending.key === 'scale') newSlots.scaleLabel = text
        if (pending.key === 'domain') newSlots.domain = text
        this.slots = newSlots
        this.input = ''
        this.pendingOptions = null
        this.pendingUserText = text
        this.flow(text)
        return
      }
      this.input = ''
      this.pendingOptions = null
      this.pendingReplace = null
      this.pendingUserText = text
      this.flow(text)
    },
    async flow (inputText) {
      this.phase = 'analyzing'
      this.thinkingText = '正在解析您的咨询要素…'
      this.startThinking()
      try {
        const res = await analyzeConsultInput({
          sessionId: this.sessionId,
          input: inputText,
          question: this.slots.question,
          country: this.slots.country,
          industry: this.slots.industry,
          scale: this.slots.scale,
          domain: this.slots.domain
        })
        if (res.slots) this.applySlots(res.slots)

        // 后续轮次调整国家 / 行业 / 规模 / 领域属内部要素变更，不在对话中提示，直接按新要素重新作答
        if (!res.ready) {
          this.stopThinking()
          this.phase = 'idle'
          this.pushMsg('guide', res.message || '请补充完整信息后再提交。')
          this.showPicker(res.missing, res.options)
          return
        }
        await this.ask()
      } catch (e) {
        this.stopThinking()
        this.phase = 'idle'
        this.pushMsg('error', '请求失败：' + ((e && e.message) || '网络异常') + '。请稍后重试。')
      }
    },
    applySlots (slots) {
      this.slots = {
        question: slots.question || '',
        country: slots.country || '',
        industry: slots.industry || '',
        scale: slots.scale || '',
        scaleLabel: slots.scaleLabel || '',
        domain: slots.domain || ''
      }
    },
    ask () {
      this.phase = 'thinking'
      this.buffer = ''
      this.streamEnded = false
      this.pendingError = ''
      this.askStart = Date.now()
      this.thinkingIdx = 0
      this.thinkingText = THINKING_STAGES[0]
      this.startThinking()
      this.currentId = ''

      const ctl = streamConsultAnswer({
        sessionId: this.sessionId,
        input: '',
        question: this.slots.question,
        country: this.slots.country,
        industry: this.slots.industry,
        scale: this.slots.scale,
        domain: this.slots.domain,
        // 带上之前轮次的问答，让模型理解追问与参数变更的语境
        history: this.history.slice()
      }, {
        meta: d => {
          if (d && d.thinkingDelayMs) this.thinkingDelayMs = d.thinkingDelayMs
        },
        thinking: d => {
          if (d && d.stage && this.phase === 'thinking') this.thinkingText = d.stage
        },
        notice: d => {
          if (!d || !d.message) return
          // 内部过程（要素变更、主备模型切换、重试等）不在前端展示，用户无需感知
          if (INTERNAL_NOTICE_WORDS.some(w => d.message.indexOf(w) !== -1)) return
          this.pushMsg('notice', d.message)
        },
        reset: () => {
          // 后端在单个模型中途失败后静默切换到下一个模型：移除残留气泡，回到思考态只保留一个执行中提示
          this.clearTimers()
          const msg = this.findMsg(this.currentId)
          if (msg) {
            this.messages = this.messages.filter(m => m.id !== msg.id)
          }
          this.currentId = ''
          this.buffer = ''
          this.phase = 'thinking'
          this.thinkingText = '正在生成建议与出处…'
        },
        guide: d => {
          this.dropCurrentIfEmpty()
          this.pushMsg('guide', (d && d.message) || '请补充完整信息。')
          if (d && d.slots) this.applySlots(d.slots)
          this.showPicker(d && d.missing, d && d.options)
        },
        reject: d => {
          this.dropCurrentIfEmpty()
          this.pushMsg('reject', (d && d.message) || '该问题超出所选专业服务领域的受理范围。')
        },
        delta: d => {
          if (!d || !d.text) return
          this.buffer += d.text
          this.scheduleTyping()
        },
        error: d => {
          this.pendingError = (d && d.text) || '生成过程出现异常'
        },
        done: d => {
          if (d && d.disclaimer) this.disclaimer = d.disclaimer
          // 收到 done 事件即标记结束，避免依赖 HTTP 连接关闭时机导致按钮卡在「生成中」
          this.streamEnded = true
          if (!this.typeTimer && !this.typeStartTimer) this.finish()
        }
      })
      this.streamCtl = ctl

      return ctl.done
        .catch(e => {
          if (e && e.name === 'AbortError') return
          this.pendingError = '连接中断：' + ((e && e.message) || '网络异常')
        })
        .then(() => {
          this.streamEnded = true
          // 没有任何正文（拒答/引导/异常）时立即收尾
          if (!this.typeTimer && !this.typeStartTimer) this.finish()
        })
    },
    // ---------- 思考中提醒 ----------
    startThinking () {
      this.stopThinking()
      this.thinkingTimer = setInterval(() => {
        this.thinkingIdx = (this.thinkingIdx + 1) % THINKING_STAGES.length
        this.thinkingText = THINKING_STAGES[this.thinkingIdx]
      }, 1800)
    },
    stopThinking () {
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer)
        this.thinkingTimer = null
      }
    },
    // ---------- 打字机输出（先满足最短思考提示时长） ----------
    scheduleTyping () {
      if (this.typeTimer || this.typeStartTimer) return
      const wait = Math.max(0, this.thinkingDelayMs - (Date.now() - this.askStart))
      this.typeStartTimer = setTimeout(() => {
        this.typeStartTimer = null
        this.beginTyping()
      }, wait)
    },
    beginTyping () {
      this.stopThinking()
      this.phase = 'streaming'
      // 首次出字时才创建 AI 气泡，避免在「思考中」阶段出现一个空闪烁框
      if (!this.currentId) {
        const msg = this.pushMsg('ai', '')
        msg.streaming = true
        this.currentId = msg.id
      }
      if (this.typeTimer) return
      this.typeTimer = setInterval(this.tick, 32)
    },
    tick () {
      const msg = this.findMsg(this.currentId)
      if (!msg) {
        this.finish()
        return
      }
      if (this.buffer.length) {
        // 缓冲越多吐字越快，保证不落后于模型
        const step = Math.max(2, Math.ceil(this.buffer.length / 30))
        msg.text += this.buffer.slice(0, step)
        this.buffer = this.buffer.slice(step)
        msg.html = renderMd(msg.text)
        this.scrollBottom()
      } else if (this.streamEnded) {
        this.finish()
      }
    },
    finish () {
      this.clearTimers()
      const msg = this.findMsg(this.currentId)
      if (msg) {
        msg.streaming = false
        if (!msg.text) {
          this.messages = this.messages.filter(m => m.id !== msg.id)
        } else {
          msg.html = renderMd(msg.text)
        }
      }
      // 本轮问答写入上下文，供下一轮追问使用（拒答 / 引导等无正文场景只记录提问，保持语境连贯）
      this.commitHistory(msg && msg.text ? msg.text : '')
      if (this.pendingError) {
        this.pushMsg('error', this.pendingError)
        this.pendingError = ''
      }
      this.currentId = ''
      this.phase = 'idle'
      this.scrollBottom()
    },
    commitHistory (answer) {
      // 用户主动停止生成时不写入，避免残缺答案污染后续追问
      if (this.skipHistory) {
        this.skipHistory = false
        this.pendingUserText = ''
        return
      }
      if (this.pendingUserText) {
        this.history.push({ role: 'user', content: this.pendingUserText })
        this.pendingUserText = ''
      }
      if (answer) {
        this.history.push({ role: 'assistant', content: answer })
      }
      // 只保留最近若干条，避免请求体过大
      if (this.history.length > 16) {
        this.history = this.history.slice(this.history.length - 16)
      }
    },
    dropCurrentIfEmpty () {
      const msg = this.findMsg(this.currentId)
      if (msg && !msg.text) {
        this.messages = this.messages.filter(m => m.id !== msg.id)
        this.currentId = ''
      }
    },
    stop () {
      if (this.streamCtl) this.streamCtl.abort()
      this.streamEnded = true
      this.buffer = ''
      const msg = this.findMsg(this.currentId)
      if (msg && msg.text) msg.text += '\n\n（已停止生成，以上内容可能不完整）'
      // 主动停止：残缺答案不写入多轮上下文，避免污染后续追问
      this.skipHistory = true
      this.finish()
    },
    clearTimers () {
      this.stopThinking()
      if (this.typeStartTimer) {
        clearTimeout(this.typeStartTimer)
        this.typeStartTimer = null
      }
      if (this.typeTimer) {
        clearInterval(this.typeTimer)
        this.typeTimer = null
      }
    }
  }
}
</script>

<style scoped>
/* ============ 主体布局 ============ */
.c-root {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.c-main {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

/* ============ 聊天区 ============ */
.c-chat {
  flex: 1 1 auto;
  min-width: 0;
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.c-chat-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(135deg, var(--blue-soft-2), #fff);
}
.c-chat-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-main);
}
.c-restart {
  margin-left: auto;
  font-size: 13px;
  color: var(--primary);
  cursor: pointer;
}
.c-restart:hover {
  text-decoration: underline;
}
.c-msgs {
  height: 60vh;
  flex: none;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fbfcff;
}
.c-msg {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.c-msg.is-user {
  justify-content: flex-end;
}
.c-avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-light), var(--primary));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.c-avatar.user {
  background: linear-gradient(135deg, #8c9ab8, #5b6b8c);
}
.c-bubble {
  max-width: calc(100% - 60px);
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.85;
  color: var(--text-main);
  box-shadow: 0 2px 10px rgba(36, 68, 203, 0.05);
}
.is-user .c-bubble {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.is-guide .c-bubble {
  background: #f6ffed;
  border-color: #b7eb8f;
}
.is-reject .c-bubble {
  background: #fff1f0;
  border-color: #ffa39e;
}
.is-error .c-bubble,
.is-notice .c-bubble {
  background: #fffbe6;
  border-color: #ffe58f;
}
.c-bubble-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #389e0d;
  margin-bottom: 6px;
}
.c-bubble-tag.warn {
  color: #cf1322;
}
.c-bubble-foot {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--border);
  font-size: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.c-foot-warn {
  color: #d48806;
}
.c-bubble.thinking {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-sub);
}
.thinking-text {
  font-size: 13.5px;
}
.dots {
  display: inline-flex;
  gap: 4px;
}
.dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary);
  animation: blink 1.2s infinite ease-in-out;
}
.dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes blink {
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}
.c-caret {
  display: inline-block;
  width: 7px;
  height: 15px;
  background: var(--primary);
  vertical-align: -2px;
  animation: caret 0.9s steps(1) infinite;
}
@keyframes caret {
  50% { opacity: 0; }
}

/* markdown 正文 */
.c-md >>> h3 {
  font-size: 16px;
  font-weight: 800;
  color: var(--primary);
  margin: 16px 0 8px;
  padding-left: 10px;
  border-left: 4px solid var(--primary);
}
.c-md >>> h4 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-main);
  margin: 12px 0 6px;
}
.c-md >>> h5 {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-sub);
  margin: 10px 0 6px;
}
.c-md >>> p {
  margin: 6px 0;
}
.c-md >>> p.md-num {
  padding-left: 2px;
}
.c-md >>> ul {
  margin: 6px 0 6px 18px;
}
.c-md >>> li {
  margin: 4px 0;
}
.c-md >>> code {
  background: var(--blue-soft);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 13px;
}
.c-md >>> a {
  color: var(--primary);
  word-break: break-all;
}
.is-user .c-md >>> a {
  color: #fff;
}

/* ============ 快捷选项 ============ */
.c-picker {
  padding: 14px 20px;
  border-top: 1px solid var(--border);
  background: var(--blue-soft-2);
}
.c-picker-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main);
  margin-bottom: 10px;
}
.c-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.c-chip {
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 20px;
  background: #fff;
  border: 1px solid #d6e0ff;
  color: var(--primary);
  cursor: pointer;
  transition: all 0.2s ease;
}
.c-chip:hover {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}
.c-chip.active {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}
.c-chips.small .c-chip {
  font-size: 12.5px;
  padding: 5px 10px;
}

/* ============ 输入区 ============ */
.c-input {
  border-top: 1px solid var(--border);
  padding: 14px 20px 18px;
  background: #fff;
}
.c-input textarea {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--text-main);
  resize: vertical;
  outline: none;
  font-family: inherit;
  background: #fbfcff;
}
.c-input textarea:focus {
  border-color: var(--primary-light);
  background: #fff;
}
.c-input-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}
.c-input-tip {
  font-size: 12px;
  color: #9aa6c0;
}
.c-btns {
  display: flex;
  gap: 10px;
}
.btn-ghost {
  padding: 9px 16px;
  border-radius: 8px;
  border: 1px solid #d6e0ff;
  background: #fff;
  color: var(--primary);
  font-size: 14px;
  cursor: pointer;
}
.btn-ghost:hover {
  background: var(--blue-soft);
}
.btn-primary {
  padding: 9px 28px;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:disabled {
  background: #a8abb2;
  cursor: not-allowed;
}

/* ============ 右侧要素面板 ============ */
.c-side {
  flex: 0 0 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 20px;
}
.c-side-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--shadow);
}
.c-side-card.note {
  background: linear-gradient(160deg, #fffdf7, #fff);
  border-color: #ffe58f;
}
.c-side-title {
  font-size: 15.5px;
  font-weight: 800;
  color: var(--text-main);
  margin-bottom: 8px;
}
.c-side-desc {
  font-size: 12.5px;
  color: var(--text-sub);
  line-height: 1.7;
  margin-bottom: 14px;
}
.c-slot {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fbfcff;
}
.c-slot-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.c-slot-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main);
}
.c-slot-state {
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 10px;
}
.c-slot-state.done {
  background: #f6ffed;
  color: #389e0d;
  border: 1px solid #b7eb8f;
}
.c-slot-state.todo {
  background: #fff1f0;
  color: #cf1322;
  border: 1px solid #ffa39e;
}
.c-slot-value {
  font-size: 13px;
  color: var(--text-main);
  line-height: 1.6;
  word-break: break-all;
}
.c-slot-empty {
  font-size: 12px;
  color: #9aa6c0;
  line-height: 1.6;
}
.c-slot-ops {
  margin-top: 6px;
  font-size: 12px;
  color: var(--primary);
  cursor: pointer;
}
.c-side-block {
  margin-top: 16px;
}
.c-side-sub {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main);
  margin-bottom: 8px;
}
.c-sample {
  font-size: 12.5px;
  color: var(--text-sub);
  line-height: 1.6;
  padding: 8px 10px;
  border: 1px dashed #d6e0ff;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}
.c-sample:hover {
  background: var(--blue-soft);
  color: var(--primary);
}
.c-note-list {
  margin: 0 0 12px 18px;
  padding: 0;
}
.c-note-list li {
  font-size: 12.5px;
  color: var(--text-sub);
  line-height: 1.9;
}
.c-note-tip {
  font-size: 12px;
  color: #ad6800;
  line-height: 1.75;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 10px;
}

/* ============ 响应式 ============ */
@media (max-width: 1024px) {
  .c-main {
    flex-direction: column;
  }
  .c-side {
    flex: 0 0 auto;
    width: 100%;
    position: static;
  }
  .c-msgs {
    height: 56vh;
  }
}

/* ============ 内嵌模式（小程序 web-view）：侧栏隐藏、充满父容器 ============ */
.c-root.is-embed {
  height: 100%;
}
.c-root.is-embed .c-main {
  height: 100%;
  align-items: stretch;
}
.c-root.is-embed .c-chat {
  height: 100%;
}
.c-root.is-embed .c-msgs {
  height: auto;
  flex: 1 1 auto;
}
@media (max-width: 1024px) {
  .c-root.is-embed .c-msgs {
    height: auto;
    flex: 1 1 auto;
  }
}
</style>
